import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Component, inject, signal } from '@angular/core';
import { ButtonDirective } from '../primitives/button/button.directive';

/** Content and action shown by a confirmation dialog. */
export interface ConfirmationDialogData {
  confirmTitle: string;
  confirmMessage: string;
  confirmLabel?: string;
  onConfirm: () => Promise<void>;
}

/** Runs a reusable confirmation action inside a CUI dialog. */
@Component({
  selector: 'cui-confirmation-dialog',
  standalone: true,
  imports: [ButtonDirective],
  templateUrl: './confirmation-dialog.component.html',
  styleUrl: './confirmation-dialog.component.css',
})
export class ConfirmationDialogComponent {
  /** Services. */
  private readonly dialogRef = inject<DialogRef<boolean>>(DialogRef);
  readonly confirmData = inject<ConfirmationDialogData>(DIALOG_DATA);

  /** State. */
  readonly isLoading = signal(false);
  readonly error = signal('');

  /** Runs the confirmed action once and reports failures in the dialog. */
  async onConfirm(): Promise<void> {
    if (this.isLoading()) return;
    this.isLoading.set(true);
    this.error.set('');
    try {
      await this.confirmData.onConfirm();
      this.dialogRef.close(true);
    } catch (error) {
      console.error('Confirmation action failed', error);
      this.error.set('Could not complete this action. Please try again.');
    } finally {
      this.isLoading.set(false);
    }
  }

  /** Closes the dialog without running the action. */
  onCancel(): void {
    this.dialogRef.close(false);
  }
}
